import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { rutaLimpia } from '../lib/gemelaDeIdioma';
// Remate post-t7-2a (R5): la barra y el pie de la portada, solo en la web (la compuerta de la app
// vive en ConBarraYPie, junto a la pieza: esta pagina no cablea IS_APP_BUILD).
import { ConBarraYPie } from './LandingPage';

/*
 * Public, indexable pricing page served at /pricing (outside ProtectedRoute).
 * The authenticated upgrade flow lives at /upgrade (PricingPage.js). This page
 * REPLICATES the plan data (it does not import from PricingPage.js) so the two
 * stay decoupled. Plan names/descriptions/feature labels reuse the `pricing`
 * i18n namespace; the per-plan flags below mirror PricingPage's FEATURES table.
 */

const ORANGE = '#FF6B00';
const ORANGE_ACCENT = '#ff8c32';
const GREEN = '#22c55e';
const GREEN_BG = 'rgba(34,197,94,0.12)';

// Feature flags per plan — must stay in sync with PricingPage.js FEATURES (data replica, not import).
//
// AS-6d — el renglón de OFFLINE, arriba de todo (mismo orden que en `/upgrade`). Esta página
// es 100% WEB y por eso la clave va FIJA en la variante web: la web no tiene modo offline —el
// offline vive en la bodega cifrada de la app—, así que el texto lleva la coletilla "in our
// app" para no prometer algo falso en el navegador. La ruta está registrada también en el
// build de app pero sin camino que llegue a ella (censo R6); si alguien aterrizara, la
// variante web sigue siendo verdad, así que aquí NO se gatea por `IS_APP_BUILD`.
//
// `free: false` = el molde de la casa para lo exclusivo de Pro (igual que
// `image_diagram_analysis`): tache y gris apagado en la tarjeta Free, nunca palomita.
//
// precios-sabi (firmas #713 y #714, 25-oct): la fila de Sabi es la UNICA con texto por plan
// (`porPlan`): la tarjeta Free pinta `features.tutor_free` y la Pro `features.tutor_pro`, con
// palomita en las dos y sin numero en negritas (la palabra "Sabi" si va en negritas: ver
// `conSabiEnNegritas`). Las demas filas se pintan como siempre.
const FEATURES = [
  { key: 'offline_studying_web',       free: false, pro: true },
  { key: 'processing_credits',         free: '5',   pro: '50' },
  { key: 'active_documents',           free: '5',   pro: '30' },
  { key: 'tutor',                      free: true,  pro: true,  porPlan: true },
  { key: 'adaptive_study_mode',        free: true,  pro: true },
  { key: 'exam_simulator',             free: true,  pro: true },
  { key: 'flashcards',                 free: true,  pro: true },
  { key: 'full_adaptive_algorithm',    free: true,  pro: true },
  { key: 'document_progress_tracking', free: true,  pro: true },
  { key: 'per_document_analytics',     free: true,  pro: true },
  { key: 'image_diagram_analysis',     free: false, pro: true },
  { key: 'topic_mastery_tracking',     free: true,  pro: true },
  { key: 'exam_readiness_indicator',   free: true,  pro: true },
];

// precios-sabi, remate (firma #715): en la fila de Sabi SOLO la palabra "Sabi" va en negritas y en
// blanco, con el estilo de los numeros de esta tabla. Un texto que no empieza con "Sabi" se pinta
// como siempre; los locales no llevan marca ninguna.
function conSabiEnNegritas(texto) {
  if (typeof texto !== 'string' || !texto.startsWith('Sabi')) return texto;
  return (
    <>
      <span style={{ color: '#fff', fontWeight: 700 }}>Sabi</span>
      {texto.slice(4)}
    </>
  );
}

const PLANS = [
  { key: 'free', featured: false },
  { key: 'pro',  featured: true },
];

const PRO_PRICING = { monthly: { display: '$8.99' }, annual: { display: '$7.49' } };

// AS-3 — los importes que van a las claves con cifra. Antes vivían DENTRO del texto de
// `pricing:annual.*`; ahora esas claves llevan placeholder (la app de iOS tiene que poder
// poner el precio localizado de Apple) y esta página, que es 100% web, pone exactamente
// los mismos números de siempre. Texto pintado idéntico.
// ajustes-1-3-0 (Bloque A3, #497): aquí vivía también `WEB_PACK_PER_CREDIT = '$0.22'` para
// `pricing:packs.best_value`; el chip dice solo "Best value" / "Mejor valor" y la llave ya
// no lleva placeholder, así que la constante se retiró.
const WEB_ANNUAL_TOTAL = '$89.90';
const WEB_ANNUAL_SAVE = '$17.98';

const PACKS_DISPLAY = [
  { credits: 15, price: '4.99', best: false },
  { credits: 40, price: '8.99', best: true },
];

// JSON-LD offers (USD): Free + Pro monthly + Pro annual (Premium retired).
const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Quizessa',
  description: 'AI-generated study questions, explanations, and flashcards from your own documents.',
  brand: { '@type': 'Brand', name: 'Quizessa' },
  // remate 2 de blog-es (Google Search Console): la imagen (el logo que ya declara la Organizacion
  // en public/index.html) y la disponibilidad de cada oferta. Nada mas: ni envio, ni devoluciones,
  // ni calificaciones.
  image: 'https://quizessa.com/logo512.png',
  offers: [
    { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD', url: 'https://quizessa.com/pricing', availability: 'https://schema.org/InStock' },
    { '@type': 'Offer', name: 'Pro', price: '8.99', priceCurrency: 'USD', description: 'Pro plan with 50 credits per month', url: 'https://quizessa.com/pricing', availability: 'https://schema.org/InStock' },
    { '@type': 'Offer', name: 'Pro (Annual)', price: '89.90', priceCurrency: 'USD', description: 'Pro plan billed annually, 50 credits per month', url: 'https://quizessa.com/pricing', availability: 'https://schema.org/InStock' },
  ],
};

function CheckMark() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', flexShrink: 0, background: 'rgba(255,140,50,0.1)' }}>
      <svg width="9" height="7" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke={ORANGE_ACCENT} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function CrossMark() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', flexShrink: 0, background: 'rgba(255,255,255,0.02)' }}>
      <svg width="7" height="7" viewBox="0 0 8 8" fill="none">
        <path d="M1 1L7 7M7 1L1 7" stroke="#2a2a2a" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </span>
  );
}

export default function PublicPricingPage() {
  // `landing`: ahi viven los textos de la barra y el pie de la portada (remate post-t7-2a, R5).
  const { t, ready, i18n } = useReadyTranslation(['pricing', 'common', 'landing']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billing, setBilling] = useState('monthly');

  // Mismo patrón de gate que LandingPage (:336): sin esto, el montaje pinta las
  // llaves crudas de i18n un instante (public.hero_title...) — visible sobre todo
  // encima del HTML pre-armado. null a propósito (no LoadingGate): un beat en
  // blanco es mejor que llaves.
  if (!ready) return null;

  const goPacks = () => navigate(user ? '/upgrade' : '/signup');
  // post-t7-2a (bloque F, diseno 3.4 c): la misma pagina vive tambien en `/es/precios` (con el
  // idioma forzado a espanol, components/RutasEnEspanol.js); su canonica es la de SU direccion.
  const canonica = rutaLimpia(window.location.pathname) === '/es/precios'
    ? 'https://quizessa.com/es/precios'
    : 'https://quizessa.com/pricing';

  return (
    <ConBarraYPie t={t} idioma={i18n?.language}>
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#fff', padding: '48px 16px' }} data-testid="public-pricing-page">
      <Helmet>
        <title>{t('pricing:public.meta_title')}</title>
        <meta name="description" content={t('pricing:public.meta_description')} />
        <link rel="canonical" href={canonica} />
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>

      {/* Hero (unchanged) */}
      <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 18px' }}>
        <h1 style={{ fontSize: 30, fontWeight: 700, margin: '0 0 8px', lineHeight: 1.2 }} data-testid="public-pricing-title">
          {t('pricing:public.hero_title')}
        </h1>
        <p style={{ fontSize: 15, color: '#888', margin: 0, lineHeight: 1.4 }}>
          {t('pricing:public.hero_subtitle')}
        </p>
      </div>

      {/* Billing toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 999, padding: 4 }} data-testid="public-billing-toggle">
          <button
            type="button" aria-pressed={billing === 'monthly'} onClick={() => setBilling('monthly')}
            style={{ padding: '8px 20px', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 14, transition: 'background 0.15s, color 0.15s', fontWeight: billing === 'monthly' ? 600 : 500, background: billing === 'monthly' ? ORANGE : 'transparent', color: billing === 'monthly' ? '#fff' : '#999' }}
            data-testid="public-billing-monthly"
          >
            {t('pricing:billing_toggle.monthly')}
          </button>
          <button
            type="button" aria-pressed={billing === 'annual'} onClick={() => setBilling('annual')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 14, transition: 'background 0.15s, color 0.15s', fontWeight: billing === 'annual' ? 600 : 500, background: billing === 'annual' ? ORANGE : 'transparent', color: billing === 'annual' ? '#fff' : '#999' }}
            data-testid="public-billing-annual"
          >
            {t('pricing:billing_toggle.annual')}
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600, background: billing === 'annual' ? 'rgba(0,0,0,0.30)' : GREEN_BG, color: billing === 'annual' ? '#fff' : GREEN, transition: 'background 0.15s, color 0.15s' }}>
              {t('pricing:billing_toggle.annual_badge')}
            </span>
          </button>
        </div>
      </div>

      {/* Plan cards (2-up) */}
      <div
        className="public-pricing-grid"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 20, maxWidth: 720, margin: '0 auto' }}
        data-testid="public-plan-cards"
      >
        {PLANS.map((plan) => {
          const isPro = plan.key === 'pro';
          const price = isPro ? PRO_PRICING[billing].display : '$0';
          const showAnnualDetail = isPro && billing === 'annual';
          return (
            <div
              key={plan.key}
              className={`public-plan-card ${isPro ? 'public-plan-card--pro' : ''}`}
              style={{
                position: 'relative',
                background: plan.featured ? '#161311' : '#111',
                border: `${plan.featured ? '1.5px' : '1px'} solid ${plan.featured ? ORANGE : '#262626'}`,
                borderRadius: 18, padding: '22px 22px', display: 'flex', flexDirection: 'column',
                boxShadow: plan.featured ? '0 0 40px -12px rgba(255,107,0,0.22)' : 'none',
              }}
              data-testid={`public-plan-card-${plan.key}`}
            >
              {plan.featured && (
                <span style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: ORANGE, color: '#fff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, padding: '5px 18px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                  {t('pricing:badge.recommended')}
                </span>
              )}

              <div style={{ fontSize: 13, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', color: isPro ? ORANGE_ACCENT : '#999', marginBottom: 10 }}>
                {t(`pricing:plans.${plan.key}.name`)}
              </div>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 44, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{price}</span>
                <span style={{ fontSize: 14, color: '#888' }}>{t(`pricing:period.${isPro ? 'month' : 'forever'}`)}</span>
                <span style={{ fontSize: 11, color: '#666' }}>USD</span>
              </div>

              {isPro && (
                <div style={{ marginTop: 8, minHeight: 24 }} data-testid="public-annual-detail">
                  {showAnnualDetail ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: '#999' }}>{t('pricing:annual.billed_line', { price: WEB_ANNUAL_TOTAL })}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: GREEN_BG, color: GREEN }}>
                        {t('pricing:annual.save_chip', { amount: WEB_ANNUAL_SAVE })}
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#999' }} data-testid="public-annual-monthly-hint">{t('pricing:annual.monthly_hint', { price: WEB_ANNUAL_TOTAL })}</div>
                  )}
                </div>
              )}

              {/* TANDA 28d (§F) — aquí vivía el párrafo de descripción del plan. Se
                  eliminó en las tres superficies a la vez; la raya se queda con los
                  14 px que el párrafo tenía por encima (ver PricingPage.js). El
                  JSON-LD de esta página NO cambia: es una constante de módulo sin
                  relación con estas llaves, y su hash está clavado en
                  `tarjetasSinDescripcion.test.js`. */}
              <div style={{ height: 1, background: '#1f1f1f', marginTop: 14, marginBottom: 8 }} />

              <div style={{ flex: 1, marginBottom: 14 }}>
                {FEATURES.map((feat) => {
                  const val = feat[plan.key];
                  const included = val === true || typeof val === 'string';
                  return (
                    <div key={feat.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 13 }}>
                      {included ? <CheckMark /> : <CrossMark />}
                      <span style={{ color: included ? '#ddd' : '#3a3a3a' }}>
                        {typeof val === 'string' ? (
                          <>
                            <span style={{ color: '#fff', fontWeight: 700 }}>{val}</span>
                            <span> {t(`pricing:features.${feat.key}`)}</span>
                          </>
                        ) : feat.porPlan ? (
                          conSabiEnNegritas(t(`pricing:features.${feat.key}_${plan.key}`))
                        ) : (
                          t(`pricing:features.${feat.key}`)
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => navigate(user ? '/upgrade' : '/signup')}
                style={{
                  width: '100%', padding: '11px 0', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  border: isPro ? 'none' : `1px solid ${ORANGE}`,
                  background: isPro ? ORANGE : 'transparent',
                  color: isPro ? '#fff' : ORANGE,
                }}
                data-testid={`public-plan-cta-${plan.key}`}
              >
                {isPro ? t('pricing:cta.upgrade_to', { plan: t('pricing:plans.pro.name') }) : t('pricing:public.cta_create_account')}
              </button>
            </div>
          );
        })}
      </div>

      {/* Discovery link — surfaces the packs section (below the fold on desktop) */}
      <div style={{ textAlign: 'center', marginTop: 14 }}>
        <button
          onClick={() => document.getElementById('public-packs-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#999', padding: 0, transition: 'color 0.15s' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = ORANGE; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#999'; }}
          data-testid="public-packs-discover-link"
        >
          {t('pricing:packs.discover_link')}
        </button>
      </div>

      {/* Credit packs section */}
      <div id="public-packs-section" style={{ maxWidth: 720, margin: '24px auto 0', textAlign: 'center', scrollMarginTop: 16 }} data-testid="public-packs-section">
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 14 }}>{t('pricing:packs.section_title')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }} className="public-packs-grid">
          {PACKS_DISPLAY.map((pack) => (
            <button
              key={pack.credits}
              onClick={goPacks}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, background: '#111', border: '1px solid #262626', borderRadius: 14, padding: 16, cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = ORANGE; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#262626'; }}
              data-testid={`public-pack-card-${pack.credits}`}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>{pack.credits}</span>
                <span style={{ fontSize: 13, color: '#999' }}>{t('pricing:packs.credits_word')}</span>
              </div>
              <span style={{ fontSize: 14, color: '#ccc' }}>${pack.price} <span style={{ fontSize: 11, color: '#666' }}>USD</span></span>
              {pack.best && (
                <span style={{ marginTop: 4, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: GREEN_BG, color: GREEN }}>
                  {t('pricing:packs.best_value')}
                </span>
              )}
            </button>
          ))}
        </div>
        <p style={{ fontSize: 13, color: '#999', marginTop: 14, lineHeight: 1.5 }}>{t('pricing:packs.honest_line')}</p>
      </div>

      {/* CTAs */}
      <div style={{ maxWidth: 720, margin: '28px auto 0', display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }} data-testid="public-pricing-ctas">
        {user ? (
          <button
            onClick={() => navigate('/upgrade')}
            style={{ padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: 'none', background: ORANGE, color: '#fff', cursor: 'pointer' }}
            data-testid="public-cta-upgrade"
          >
            {t('pricing:public.cta_go_upgrade')}
          </button>
        ) : (
          <>
            <button
              onClick={() => navigate('/signup')}
              style={{ padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: 'none', background: ORANGE, color: '#fff', cursor: 'pointer' }}
              data-testid="public-cta-signup"
            >
              {t('pricing:public.cta_create_account')}
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{ padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 500, background: 'transparent', color: ORANGE, border: `1px solid ${ORANGE}`, cursor: 'pointer' }}
              data-testid="public-cta-login"
            >
              {t('pricing:public.cta_login')}
            </button>
          </>
        )}
      </div>

      {/* Responsive: stack cards on narrow screens, Pro first */}
      <style>{`
        @media (max-width: 768px) {
          .public-pricing-grid { grid-template-columns: 1fr !important; }
          .public-plan-card--pro { order: -1; }
        }
      `}</style>
    </div>
    </ConBarraYPie>
  );
}
